import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaBoxOpen,
  FaCashRegister,
  FaChartBar,
  FaClipboardList,
  FaHome,
  FaMoneyBillWave,
  FaReceipt,
  FaShieldAlt,
  FaTachometerAlt,
  FaUserFriends,
  FaUtensils,
  FaWarehouse,
} from "react-icons/fa";
import "./Sidebar.css";

const SIDEBAR_W = 200;

function Sidebar({ usuario }) {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const sidebarRef = useRef(null);

  const permisosDefinidos = Array.isArray(usuario?.permisos);
  const permisos = permisosDefinidos ? usuario.permisos : [];
  const fallbackSuperadmin = usuario?.rol === "superadmin";
  const fallbackVendedorPos = !permisosDefinidos && usuario?.rol === "vendedor";
  const tiene = (permiso) =>
    permisos.includes(permiso) ||
    fallbackSuperadmin ||
    (fallbackVendedorPos && permiso === "pos");

  const isDesktop = () => window.matchMedia("(min-width: 1024px)").matches;
  const toggleMenu = () => setVisible((v) => !v);
  const closeMenu = () => setVisible(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (visible && sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        if (!isDesktop()) closeMenu();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [visible]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeMenu();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  useEffect(() => {
    const applyPadding = () => {
      document.body.style.paddingLeft = visible && isDesktop() ? `${SIDEBAR_W}px` : "0px";
    };
    applyPadding();
    window.addEventListener("resize", applyPadding);
    return () => {
      window.removeEventListener("resize", applyPadding);
      document.body.style.paddingLeft = "0px";
    };
  }, [visible]);

  const linkAutoCloseProps = isDesktop() ? {} : { onClick: closeMenu };

  const puedeInventario = tiene("inventario") || tiene("insumos");

  const items = [
    tiene("pos") && { to: "/pos", label: "Panel Principal", icon: FaHome },
    tiene("arqueos") && { to: "/arqueos", label: "Arqueos", icon: FaReceipt },
    tiene("cierres") && { to: "/cierres", label: "Cierres de caja", icon: FaCashRegister },
    tiene("informes") && { to: "/informes", label: "Informes", icon: FaChartBar },
    tiene("insumos") && { to: "/insumos", label: "Insumos", icon: FaBoxOpen },
    puedeInventario && { to: "/inventario", label: "Inventario", icon: FaWarehouse },
    tiene("movimientos") && { to: "/movimientos", label: "Movimientos", icon: FaMoneyBillWave },
    tiene("dashboard_pedidos") && { to: "/dashboard-pedidos", label: "Dashboard pedidos", icon: FaTachometerAlt },
    tiene("pedidos") && { to: "/pedidos", label: "Pedidos", icon: FaClipboardList },
    tiene("productos") && { to: "/productos", label: "Productos", icon: FaUtensils },
    tiene("roles") && { to: "/roles", label: "Roles", icon: FaShieldAlt },
    tiene("usuarios") && { to: "/usuarios", label: "Usuarios", icon: FaUserFriends },
  ].filter(Boolean);

  return (
    <>
      <button className="menu-toggle" onClick={toggleMenu} aria-label="Abrir menu">
        <span aria-hidden="true">☰</span>
      </button>

      {visible && !isDesktop() && (
        <div className="sidebar-backdrop" onClick={closeMenu} />
      )}

      <aside
        ref={sidebarRef}
        className={`sidebar ${visible ? "visible" : "hidden"}`}
        style={{ "--sidebar-w": `${SIDEBAR_W}px` }}
      >
        <div className="logo-container">
          <img src="/logolasushiteria.png" alt="Logo" className="logo" />
          <h2>La Sushiteria</h2>
        </div>

        <nav>
          <ul>
            {items.map(({ to, label, icon: Icon }) => (
              <li key={to} className={location.pathname === to ? "activo" : ""}>
                <Link to={to} {...linkAutoCloseProps}>
                  <Icon className="sidebar-icon" aria-hidden="true" />
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="cerrar-sesion-container" />
      </aside>
    </>
  );
}

export default Sidebar;
